import React, { useMemo } from 'react';
import classNames from 'classnames';
import { type LayoutName, isLayoutNameList } from '@wix/communities-blog-client-common';
import { useFeedMetadataSettings } from '../../hooks/use-feed-metadata-settings';
import useFontClassName from '../../hooks/use-font-class-name';
import useIsFeedDesignEnabled from '../../hooks/use-is-feed-design-enabled';
import usePostFontSize from '../../hooks/use-post-font-size';
import { getLayoutTitleLineCount } from '../../selectors/app-settings-selectors';
import { getShouldUsePostListMobileTitleFontSize } from '../../selectors/mobile-settings-selectors';
import { type LineCount } from '../../services/layout-config';
import alignmentStyles from '../../styles/post-list-item-alignment.scss';
import styles from '../../styles/post-list-item.scss';
import { type NormalizedPost } from '../../types';
import { useSelector } from '../runtime-context';
import { PostListItemProGalleryRatingsDisplay } from './post-list-item-pro-gallery-ratings-display';
import PostListItemProGalleryTitle from './post-list-item-pro-gallery-title';

type Props = {
  post: NormalizedPost;
  type: LayoutName | undefined;
  withoutFooter: boolean;
  withDescription: boolean;
  showMoreButton: boolean;
  lineCount?: LineCount;
  isHoverDisabled: boolean;
};

export const PostListItemProGalleryTitleDescriptionTitle = ({
  type,
  withoutFooter,
  withDescription,
  post,
  showMoreButton,
  lineCount,
  isHoverDisabled,
}: Props) => {
  const shouldUsePostListMobileTitleFontSize = useSelector(getShouldUsePostListMobileTitleFontSize);
  const titleLineCount = useSelector((state) => getLayoutTitleLineCount({ state }));
  const { postTitleFontSize, defaultPostTitleFontSize } = usePostFontSize();
  const { titleFontClassName } = useFontClassName();
  const layoutTypeClassName = type ? styles[type] : undefined;
  const { section } = useIsFeedDesignEnabled();
  const layoutList = isLayoutNameList(type);
  const feedMetadataSettings = useFeedMetadataSettings({ post });

  const styleFontSizeTitle = useMemo(
    () => ({
      fontSize: shouldUsePostListMobileTitleFontSize ? postTitleFontSize : defaultPostTitleFontSize,
    }),
    [shouldUsePostListMobileTitleFontSize, postTitleFontSize, defaultPostTitleFontSize],
  );

  const titleContainerClassName = useMemo(
    () =>
      classNames(
        styles.title,
        titleFontClassName,
        layoutTypeClassName,
        withoutFooter && styles.withoutFooter,
        !withDescription && withoutFooter && styles.withoutBottomMargin,
        !withDescription && styles.withoutDescription,
        post?.isPinned && styles.withIcons,
        showMoreButton ? styles.withShowMoreButton : styles.withoutShowMoreButton,
      ),
    [
      titleFontClassName,
      layoutTypeClassName,
      withoutFooter,
      withDescription,
      post?.isPinned,
      showMoreButton,
    ],
  );

  return (
    <div
      style={styleFontSizeTitle}
      className={classNames(titleContainerClassName, alignmentStyles.textAlign)}
      data-hook="post-list-item__title"
    >
      <PostListItemProGalleryTitle
        type={type}
        title={post?.title}
        style={styleFontSizeTitle}
        lineCount={lineCount?.title}
        titleLineCount={titleLineCount}
        showCategoryLabel={feedMetadataSettings.showCategoryLabel}
        isHoverDisabled={isHoverDisabled}
      />
      {!layoutList && (
        <PostListItemProGalleryRatingsDisplay
          post={post}
          type={type}
          withoutFooter={withoutFooter}
          section={section}
        />
      )}
    </div>
  );
};
